import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  InputAdornment,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import TitleIcon from '@mui/icons-material/Title';
import DescriptionIcon from '@mui/icons-material/Description';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhotoIcon from '@mui/icons-material/Photo';
import InfoIcon from '@mui/icons-material/Info';
import { logger } from "../utils/logger"; // Adjust the import path as necessary
import TimeSlotSelector from './TimeSlotSelector';

const GOOGLE_PLACES_FIELDS = {
  basic: [
    'name',
    'formatted_address',
    'formatted_phone_number',
    'website',
    'geometry',
    'place_id',
    'photos'  ]
};

const getPlaceDetails = async (placeId) => {
  if (!window.google) throw new Error('Google Maps not loaded');

  const placesService = new window.google.maps.places.PlacesService(
    document.createElement('div')
  );

  try {
    const place = await new Promise((resolve, reject) => {
      placesService.getDetails({
        placeId,
        fields: GOOGLE_PLACES_FIELDS.basic,
        key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      }, (result, status) => {
        if (status === 'OK') {
          resolve(result);
        } else {
          reject(new Error(`Place Details failed: ${status}`));
        }
      });
    });

    // Get photo data directly from Google Places photo object
    let photoInfo = {};
    if (place.photos?.[0]) {
      const photo = place.photos[0];
      const tempUrl = photo.getUrl({ maxWidth: 1200, maxHeight: 800 });
      photoInfo = {
        photo_reference: photo.photo_reference,
        temp_image_url: tempUrl,
        image_url: tempUrl // Use temp URL for preview
      };
      logger.log('Photo info extracted:', photoInfo);
    }

    return {
      headline: place.name || '',
      address: place.formatted_address || '',
      phone: place.formatted_phone_number || '',
      link: place.website || '',
      lat: place.geometry?.location.lat() || '',
      lon: place.geometry?.location.lng() || '',
      place_id: place.place_id || '',
      ...photoInfo
    };
  } catch (error) {
    logger.error('Error fetching place details:', error);
    throw error;
  }
};

const GoogleMapsSearch = ({ onPlaceSelected, initialSearchQuery }) => {
  const searchInputRef = useRef(null);

  useEffect(() => {
    // Always initialize Google Maps regardless of initialSearchQuery
    const initializeGoogleMaps = () => {
      if (!window.google) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.onload = setupAutocomplete;
        document.body.appendChild(script);
      } else {
        setupAutocomplete();
      }
    };

    const setupAutocomplete = () => {
      if (!searchInputRef.current) return;
      
      const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
        types: ['establishment'],
        componentRestrictions: { country: 'IL' }
      });

      autocomplete.addListener('place_changed', async () => {
        const place = autocomplete.getPlace();
        if (place.place_id) {
          try {
            const placeDetails = await getPlaceDetails(place.place_id);
            onPlaceSelected(placeDetails);
          } catch (error) {
            logger.error('Error fetching place details:', error);
          }
        }
      });

      // If we have an initial query, set the input value
      if (initialSearchQuery) {
        searchInputRef.current.value = initialSearchQuery;
      }
    };

    initializeGoogleMaps();

    return () => {
      // Cleanup event listeners if needed
      if (window.google && searchInputRef.current) {
        window.google.maps.event.clearInstanceListeners(searchInputRef.current);
      }
    };
  }, [onPlaceSelected]); // Remove initialSearchQuery from dependencies

  return (
    <TextField
      inputRef={searchInputRef}
      fullWidth
      label="Search for a business"
      placeholder="Type to search businesses in Tel Aviv..."
      defaultValue={initialSearchQuery}
      variant="outlined"
      size="small"
    />
  );
};

const VenueForm = ({ mode, venue, onSubmit, onCancel, onGooglePlaceSelected, initialSearchQuery }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [formData, setFormData] = useState({
    headline: '',
    address: '',
    phone: '',
    link: '',
    image_url: '',
    photo_reference: '',
    category: 'hh',
    lat: '',
    lon: '',
    is_active: true,
    days: '',
    main_offer: '',
    offer_details: '',
    opening_hours: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    },
    place_id: '',
    ...venue // Merge with provided venue data if it exists
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [tempImageUrl, setTempImageUrl] = useState('');

  useEffect(() => {
    setFormData({
      headline: '',
      address: '',
      phone: '',
      link: '',
      image_url: '',
      photo_reference: '',
      category: 'hh',
      lat: '',
      lon: '',
      is_active: true,
      days: '',
      main_offer: '',
      offer_details: '',
      opening_hours: {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
      },
      place_id: '',
      ...venue
    });
    
    if (venue.image_url) {
      setTempImageUrl(venue.image_url);
    }
  }, [venue]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    setFormData(newFormData);
    validateForm(newFormData);
  };

  const handleNumberChange = (event) => {
    const { name, value } = event.target;
    const newFormData = {
      ...formData,
      [name]: value === '' ? '' : Number(value)
    };
    setFormData(newFormData);
    validateForm(newFormData);
  };

  const validateForm = (data = formData) => {
    const errors = {};
    
    if (!data.headline?.trim()) errors.headline = 'Headline is required';
    if (!data.address?.trim()) errors.address = 'Address is required';
    if (!data.days?.trim()) errors.days = 'Operating days are required';
    if (!data.main_offer?.trim()) errors.main_offer = 'Main offer is required';
    if (!data.category?.trim()) errors.category = 'Category is required';
    
    if (!data.lat) errors.lat = 'Latitude is required';
    if (!data.lon) errors.lon = 'Longitude is required';
    if (isNaN(data.lat)) errors.lat = 'Latitude must be a number';
    if (isNaN(data.lon)) errors.lon = 'Longitude must be a number';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleGooglePlaceSelected = (placeData) => {
    setFormData(prev => ({
      ...prev,
      ...placeData,
      image_url: null // Clear existing image_url
    }));
    
    if (placeData.temp_image_url) {
      setTempImageUrl(placeData.temp_image_url);
    }
    
    if (onGooglePlaceSelected) {
      onGooglePlaceSelected(placeData);
    }
  };

  return (
    <>
      <DialogTitle>
        {mode === 'add' ? 'Add New Venue' : 'Edit Venue'}
      </DialogTitle>
      
      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
            <Typography variant="subtitle1" gutterBottom>
              Search for a business on Google Maps
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This will help fill in address, name, phone, website, and location coordinates automatically.
            </Typography>
            <GoogleMapsSearch 
              onPlaceSelected={handleGooglePlaceSelected} 
              initialSearchQuery={initialSearchQuery} // Pass directly from props
            />
          </Paper>
          
          {(tempImageUrl || formData.image_url) && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="subtitle2" gutterBottom>Image Preview</Typography>
              <Box
                component="img"
                src={tempImageUrl || formData.image_url}
                alt={formData.headline || "Venue"}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  border: '1px solid #eee',
                  borderRadius: 1
                }}
                onError={(e) => {
                  e.target.src = "/public/images/default_venue_photo.jpg";
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {mode === 'add' ? 
                  "The image will be downloaded and stored after saving" : 
                  "This is the stored image from Firebase Storage"}
              </Typography>
            </Box>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Venue Name"
                name="headline"
                value={formData.headline || ''}
                onChange={handleChange}
                error={!!validationErrors.headline}
                helperText={validationErrors.headline}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TitleIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Address"
                name="address"
                value={formData.address || ''}
                onChange={handleChange}
                error={!!validationErrors.address}
                helperText={validationErrors.address}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlaceIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website URL"
                name="link"
                value={formData.link || ''}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Google Photo Reference"
                name="photo_reference"
                value={formData.photo_reference || ''}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhotoIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="This reference is used to download the image when saving the venue">
                        <InfoIcon color="info" fontSize="small" />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText="Used to download the venue image from Google Places"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Image URL"
                name="image_url"
                value={formData.image_url || ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ImageIcon />
                    </InputAdornment>
                  ),
                  readOnly: true,
                }}
                disabled={true}
                helperText="This will be updated automatically when the image is downloaded"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Category"
                name="category"
                value={formData.category || 'hh'}
                onChange={handleChange}
                disabled={true}
                helperText="Category is fixed to 'Happy Hour'"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CategoryIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Latitude"
                name="lat"
                type="number"
                value={formData.lat || ''}
                onChange={handleNumberChange}
                error={!!validationErrors.lat}
                helperText={validationErrors.lat}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Longitude"
                name="lon"
                type="number"
                value={formData.lon || ''}
                onChange={handleNumberChange}
                error={!!validationErrors.lon}
                helperText={validationErrors.lon}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Place ID"
                name="place_id"
                value={formData.place_id || ''}
                onChange={handleChange}
                disabled={true}
                helperText="Automatically populated from Google Maps search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlaceIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active || false}
                    onChange={handleChange}
                    name="is_active"
                    color="primary"
                  />
                }
                label="Venue is active"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Happy Hour Details
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Operating Days/Hours (Display Text)"
                name="days"
                value={formData.days || ''}
                onChange={handleChange}
                error={!!validationErrors.days}
                helperText={validationErrors.days || "e.g., 'Sun-Thu, 18:00-21:00'"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Main Offer"
                name="main_offer"
                value={formData.main_offer || ''}
                onChange={handleChange}
                error={!!validationErrors.main_offer}
                helperText={validationErrors.main_offer || "e.g., '1+1 on all drinks'"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Offer Details"
                name="offer_details"
                value={formData.offer_details || ''}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TimeSlotSelector
                onChange={(newOpeningHours) => {
                  logger.log('TimeSlotSelector onChange called with:', newOpeningHours);
                  setFormData(prev => {
                    const updated = {
                      ...prev,
                      opening_hours: {
                        ...prev.opening_hours,
                        ...newOpeningHours
                      }
                    };
                    logger.log('Updated form data:', updated);
                    return updated;
                  });
                }}
                initialOpeningHours={formData.opening_hours}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={Object.keys(validationErrors).length > 0}
        >
          {mode === 'add' ? 'Add Venue' : 'Save Changes'}
        </Button>
      </DialogActions>
    </>
  );
};

VenueForm.defaultProps = {
  venue: {},
  mode: 'add',
  initialSearchQuery: '', // Add default prop
  onSubmit: () => {},
  onCancel: () => {},
  onGooglePlaceSelected: () => {}
};

export default VenueForm;